import { useCallback, useEffect, useMemo } from "react";
import { gridConfigFor, TILE_MAX_ZOOM, TILE_SIZE, type TheatreMap } from "@/data/maps.ts";
import type { Poi } from "@/data/pois.ts";
import { gridLabel } from "@/engine/grid.ts";
import { screenToGame, type ProjectionConfig } from "@/engine/projection.ts";
import { formatCoord, type GameCoord } from "@/engine/coords.ts";
import type { Marker, Route } from "@/session/schemas.ts";
import { GridLayer } from "./GridLayer.tsx";
import { MarkerLayer } from "./MarkerLayer.tsx";
import { OrderLayer, type OrderMarker } from "./OrderLayer.tsx";
import { PoiLayer } from "./PoiLayer.tsx";
import { RangeRingLayer } from "./RangeRingLayer.tsx";
import { RouteLayer } from "./RouteLayer.tsx";
import { TileLayer } from "./TileLayer.tsx";
import { useCamera } from "./useCamera.ts";

interface MapViewProps {
  readonly map: TheatreMap;
  readonly showGrid: boolean;
  readonly showLabels: boolean;
  readonly onSelectPoi?: ((poi: Poi) => void) | undefined;
  /**
   * Reports the callout reference under the cursor, so a commander can task a
   * squad to wherever they are pointing without reading a coordinate aloud.
   */
  readonly onCursorPlace?: ((place: string | null) => void) | undefined;
  /** Fired when the map is tapped, with the callout reference of that point. */
  readonly onPickPlace?: ((place: string, at: GameCoord) => void) | undefined;
  /**
   * Icon URL for whatever a tap on the map would place right now. Shown as
   * the cursor itself, so "what happens if I click" is answered by looking
   * at the pointer instead of reading a status line somewhere else on
   * screen.
   */
  readonly placingIconUrl?: string | undefined;
  /**
   * Fired on a right-click, with both the screen point (to anchor a context
   * menu) and the game coordinate underneath it.
   */
  readonly onContextMenuAt?:
    ((screen: { x: number; y: number }, at: GameCoord, place: string) => void) | undefined;
  /** Active squad taskings to draw. */
  readonly orders?: readonly OrderMarker[] | undefined;
  /** Commander only: tap a pin to withdraw that order. */
  readonly onRemoveOrder?: ((orderId: string) => void) | undefined;
  /** Player-placed markers to draw. */
  readonly markers?: readonly Marker[] | undefined;
  readonly myId?: string | undefined;
  /** The commander can clear anyone's marker; everyone else only their own. */
  readonly canManageAllMarkers?: boolean | undefined;
  readonly onRemoveMarker?: ((markerId: string) => void) | undefined;
  /** Committed routes / flight paths to draw. */
  readonly routes?: readonly Route[] | undefined;
  /** The route currently being drawn — live preview before committing. */
  readonly pendingRouteWaypoints?: readonly GameCoord[] | undefined;
  readonly onRemoveRoute?: ((routeId: string) => void) | undefined;
  /** Receives a jump function so a search result can move the camera. */
  readonly onReady?: ((api: MapApi) => void) | undefined;
}

export interface MapApi {
  /** Centre the map on a coordinate, zooming in if currently wide. */
  readonly goTo: (at: GameCoord) => void;
}

/** Native pyramid size in world pixels: 32 x 32 tiles of 256 px at zoom 5. */
const WORLD_SIZE = TILE_SIZE * 2 ** TILE_MAX_ZOOM;

export function MapView({
  map,
  showGrid,
  showLabels,
  onSelectPoi,
  onCursorPlace,
  onPickPlace,
  placingIconUrl,
  onContextMenuAt,
  orders,
  onRemoveOrder,
  markers,
  myId,
  canManageAllMarkers,
  onRemoveMarker,
  routes,
  pendingRouteWaypoints,
  onRemoveRoute,
  onReady,
}: MapViewProps): React.ReactElement {
  const config = useMemo<ProjectionConfig>(
    () => ({ tileBounds: map.tileBounds, worldSize: WORLD_SIZE }),
    [map.tileBounds],
  );

  const grid = useMemo(() => gridConfigFor(map), [map]);

  const { camera, viewport, containerRef, isPanning, cursor, fit, focus, zoomBy } = useCamera(
    config,
    map.playable,
  );

  useEffect(() => {
    if (!onReady) return;
    onReady({
      goTo: (at) => {
        // Jumping to a place is useless if you arrive still zoomed out to the
        // whole 16 km, so pull in to a readable scale unless already closer.
        focus(at, Math.max(camera.scale, 0.45));
      },
    });
    // `camera.scale` is read inside the closure rather than depended on, so a
    // pan does not rebuild the API on every frame.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onReady, focus]);

  useEffect(() => {
    if (!onCursorPlace) return;
    onCursorPlace(cursor ? gridLabel(cursor, grid, 1) : null);
  }, [cursor, grid, onCursorPlace]);

  /**
   * Tapping the map picks a target. A drag must not count as a pick, or every
   * pan would retarget a squad — `useCamera` flags the gesture for exactly
   * this reason.
   */
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onPickPlace) return;
      const element = containerRef.current;
      if (!element) return;
      if (element.dataset["panned"] === "true") return;

      const rect = element.getBoundingClientRect();
      const point = screenToGame(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        camera,
        config,
      );
      onPickPlace(gridLabel(point, grid, 1), point);
    },
    [onPickPlace, containerRef, camera, config, grid],
  );

  /**
   * Right-click opens the context menu instead of the browser's own — a menu
   * of "Reload", "Inspect" and "Save image as…" has never once been useful on
   * this map.
   */
  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onContextMenuAt) return;
      event.preventDefault();
      const element = containerRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const point = screenToGame(
        { x: event.clientX - rect.left, y: event.clientY - rect.top },
        camera,
        config,
      );
      onContextMenuAt({ x: event.clientX, y: event.clientY }, point, gridLabel(point, grid, 1));
    },
    [onContextMenuAt, containerRef, camera, config, grid],
  );

  return (
    <div className="relative h-full w-full overflow-hidden bg-surface-sunken">
      <div
        ref={containerRef}
        className="absolute inset-0 touch-none"
        style={{
          cursor: isPanning
            ? "grabbing"
            : placingIconUrl
              ? `url(${placingIconUrl}) 12 12, crosshair`
              : onPickPlace
                ? "crosshair"
                : "grab",
        }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        <TileLayer mapId={map.id} camera={camera} viewport={viewport} config={config} />

        {showGrid && (
          <GridLayer
            grid={grid}
            playable={map.playable}
            camera={camera}
            config={config}
            showLabels={showLabels}
          />
        )}

        <PoiLayer
          mapId={map.id}
          camera={camera}
          config={config}
          showLabels={showLabels}
          {...(onSelectPoi ? { onSelect: onSelectPoi } : {})}
        />

        {orders && orders.length > 0 && (
          <OrderLayer
            orders={orders}
            grid={grid}
            camera={camera}
            config={config}
            onRemove={onRemoveOrder}
          />
        )}

        {((routes && routes.length > 0) ||
          (pendingRouteWaypoints && pendingRouteWaypoints.length >= 2)) && (
          <RouteLayer
            routes={routes ?? []}
            pendingWaypoints={pendingRouteWaypoints}
            camera={camera}
            config={config}
            myId={myId ?? ""}
            canManageAll={canManageAllMarkers ?? false}
            onRemove={onRemoveRoute ?? (() => {})}
          />
        )}

        {markers && markers.length > 0 && (
          <>
            <RangeRingLayer markers={markers} camera={camera} config={config} />
            {onRemoveMarker && (
              <MarkerLayer
                markers={markers}
                camera={camera}
                config={config}
                myId={myId ?? ""}
                canManageAll={canManageAllMarkers ?? false}
                onRemove={onRemoveMarker}
              />
            )}
          </>
        )}
      </div>

      <MapControls
        onZoomIn={() => zoomBy(1.4)}
        onZoomOut={() => zoomBy(1 / 1.4)}
        onFit={() => fit(map.playable)}
      />

      <CursorReadout cursor={cursor} grid={grid} map={map} />
    </div>
  );
}

interface MapControlsProps {
  readonly onZoomIn: () => void;
  readonly onZoomOut: () => void;
  readonly onFit: () => void;
}

function MapControls({ onZoomIn, onZoomOut, onFit }: MapControlsProps): React.ReactElement {
  return (
    <div className="absolute top-3 right-3 flex flex-col gap-1">
      <ControlButton label="Zoom in" onClick={onZoomIn}>
        +
      </ControlButton>
      <ControlButton label="Zoom out" onClick={onZoomOut}>
        &minus;
      </ControlButton>
      <ControlButton label="Fit map" onClick={onFit}>
        ⤢
      </ControlButton>
    </div>
  );
}

function ControlButton({
  label,
  onClick,
  children,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className="touch-target flex items-center justify-center rounded border border-border-subtle bg-surface-raised/90 text-lg text-text-secondary backdrop-blur transition-colors hover:border-border-strong hover:text-text-primary"
    >
      {children}
    </button>
  );
}

interface CursorReadoutProps {
  readonly cursor: ReturnType<typeof useCamera>["cursor"];
  readonly grid: ReturnType<typeof gridConfigFor>;
  readonly map: TheatreMap;
}

/**
 * Live readout of whatever is under the pointer.
 *
 * Shows the grid callout first and the raw coordinate second, because the
 * callout is what gets said out loud and the coordinate is what gets pasted.
 */
function CursorReadout({ cursor, grid, map }: CursorReadoutProps): React.ReactElement | null {
  if (!cursor) return null;

  return (
    <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-3 rounded border border-border-subtle bg-surface-raised/90 px-3 py-1.5 backdrop-blur">
      <span className="numeric text-sm font-semibold text-accent">
        {gridLabel(cursor, grid, 1)}
      </span>
      <span className="numeric text-xs text-text-muted">{formatCoord(cursor)}</span>
      {map.playableIsEstimated && (
        <span
          className="text-xs text-warning"
          title="This map's playable area has not been calibrated, so grid references may be off."
        >
          uncalibrated
        </span>
      )}
    </div>
  );
}
